import { useEffect, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowLeft, Printer } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { getLogoPath } from '@/components/shared/Logo'
import { getBranchCurrency, getBranchCurrencyWords } from '@/lib/currency'

export type PrintParty = {
  nameEn?: string
  nameAr?: string
  address?: string
  addressAr?: string
  country?: string
  city?: string
  zip?: string
  phone?: string
  vat?: string
  otherId?: string
}

export type PrintInfoRow = {
  en: string
  ar: string
  value: string
}

export type PrintLine = {
  description?: string
  qty?: number
  unitPrice?: number
  itemPrice?: number
  taxableAmount?: number
  taxRate?: number
  taxAmount?: number
  lineAmount?: number
}

export type PrintTotals = {
  subTotal: number
  taxableAmount: number
  vatAmount: number
  total: number
  retentionPercent?: number
}

const ONES = [
  'Zero', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
  'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen',
]
const TENS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety']
const SCALE = ['', 'Thousand', 'Million', 'Billion', 'Trillion']

const convertHundreds = (num: number): string => {
  const result: string[] = []
  if (num >= 100) {
    result.push(`${ONES[Math.floor(num / 100)]} Hundred`)
    num %= 100
  }
  if (num >= 20) {
    result.push(TENS[Math.floor(num / 10)])
    if (num % 10) result.push(ONES[num % 10])
  } else if (num > 0) {
    result.push(ONES[num])
  }
  return result.join(' ')
}

const convertIntegerToWords = (value: number) => {
  if (value === 0) return ONES[0]
  let num = value
  const chunks: string[] = []
  let scaleIndex = 0
  while (num > 0) {
    const chunk = num % 1000
    if (chunk) {
      const chunkWords = convertHundreds(chunk)
      const scaleWord = SCALE[scaleIndex]
      chunks.unshift(`${chunkWords}${scaleWord ? ` ${scaleWord}` : ''}`.trim())
    }
    num = Math.floor(num / 1000)
    scaleIndex++
  }
  return chunks.join(' ')
}

export const formatPrintAmount = (value?: number) => {
  if (value === undefined || value === null || Number.isNaN(value)) return '0.00'
  return Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

export const formatDocNo = (prefix: string, raw?: string | number | null) => {
  if (raw === undefined || raw === null || String(raw).trim() === '') return `${prefix} - 00000`
  const cleaned = String(raw).trim().replace(new RegExp(`^${prefix}\\s*-?\\s*`, 'i'), '')
  const digits = cleaned.replace(/\D/g, '')
  if (!digits) return `${prefix} - ${cleaned}`
  return `${prefix} - ${digits.padStart(5, '0')}`
}

const amountToWords = (amount?: number) => {
  if (amount === undefined || amount === null || Number.isNaN(amount)) return ''
  const absolute = Math.abs(amount)
  const integerPart = Math.floor(absolute)
  const fractionPart = Math.round((absolute - integerPart) * 100)
  const integerWords = convertIntegerToWords(integerPart)
  const { major, minor } = getBranchCurrencyWords()
  if (fractionPart > 0) {
    return `${integerWords} ${major} and ${convertIntegerToWords(fractionPart)} ${minor} Only`
  }
  return `${integerWords} ${major} Only`
}

const PRINT_CSS = `
  @media print {
    @page { size: A4; margin: 8mm; }
    * { margin: 0; padding: 0; box-sizing: border-box; }
    html, body { height: auto; overflow: hidden; margin: 0; padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .no-print { display: none !important }
    .invoice-print-root { margin: 0; padding: 0; height: 100vh; border: 1px solid #ddd; position: relative; }
    .invoice-wrapper { border: 1px solid #ddd; padding: 10px; min-height: calc(100vh - 16mm); display: flex; flex-direction: column; position: relative; padding-bottom: 30px; margin: 0; }
    .invoice-content { flex: 1; }
    .invoice-footer { position: fixed; bottom: 8mm; left: 8mm; right: 8mm; border-top: 1px dashed #999; padding-top: 4px; padding-bottom: 4px; text-align: center; font-weight: 600; font-size: 9px; background: white; z-index: 1000; }
  }
  .invoice-print-root { font-family: 'Arial', 'Noto Naskh Arabic', sans-serif; color: #1f2937; border: 1px solid #ddd; }
  .invoice-wrapper { max-width: 100%; margin: 0 auto; background: #fff; padding: 10px; border: none; min-height: calc(100vh - 40px); display: flex; flex-direction: column; }
  .invoice-content { flex: 1; }
  .top-brand { display:flex; align-items:center; justify-content:space-between; gap:8px; margin-bottom: 4px }
  .company-title { font-weight:700; font-size:14px; letter-spacing:0.2px; text-align:center }
  .info-table { width:100%; border-collapse:collapse; table-layout:fixed; font-size:10px; margin-bottom: 4px }
  .info-table td { vertical-align:middle; padding:5px 8px; border:1px solid #F5F5F5 }
  .info-label { width:25%; background:#F5F5F5; font-weight:700; text-align:left }
  .info-value { width:50%; text-align:center }
  .info-label-ar { width:25%; background:#F5F5F5; font-weight:700; text-align:right }
  .items-table { width:100%; border-collapse:collapse; font-size:9px; margin-top:4px }
  .items-table th, .items-table td { border:1px solid #F5F5F5; padding:3px; vertical-align:middle; }
  .items-table thead th { background:#F5F5F5; font-weight:700; font-size:9px }
  .items-total-row { background:#F5F5F5; font-weight:800 }
  .invoice-footer { border-top:1px dashed #999; padding-top:4px; margin-top:4px; text-align:center; font-weight:600; font-size:9px }
  .rtl { direction: rtl; unicode-bidi: embed }
  @media screen {
    .invoice-wrapper { max-width: 820px; padding: 12px }
    .company-title { font-size: 16px }
    .info-table { font-size: 11px; margin-bottom: 6px }
    .info-table td { padding: 4px }
    .items-table { font-size: 10px; margin-top: 6px }
    .items-table th, .items-table td { padding: 4px }
  }
`

type PurchasePrintLayoutProps = {
  titleAr: string
  titleEn: string
  printButtonLabel: string
  infoRows: PrintInfoRow[]
  seller: PrintParty
  buyer: PrintParty
  lines: PrintLine[]
  totals: PrintTotals
  notes?: string
  footer: string
  itemAmountLabelEn?: string
  itemAmountLabelAr?: string
}

export function PurchasePrintLayout({
  titleAr,
  titleEn,
  printButtonLabel,
  infoRows,
  seller,
  buyer,
  lines,
  totals,
  notes,
  footer,
  itemAmountLabelEn = 'BILL AMT.',
  itemAmountLabelAr = 'مبلغ الفاتورة',
}: PurchasePrintLayoutProps) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const didAutoPrint = useRef(false)
  const autoPrint = searchParams.get('autoprint') === '1'
  const amountWords = amountToWords(totals.total)
  const retention = totals.retentionPercent ?? 0

  useEffect(() => {
    if (!autoPrint || didAutoPrint.current) return
    const timer = window.setTimeout(() => {
      if (didAutoPrint.current) return
      didAutoPrint.current = true
      window.print()
    }, 400)
    return () => window.clearTimeout(timer)
  }, [autoPrint])

  return (
    <div className="invoice-print-root bg-gray-50 p-6 print:bg-white print:p-0 print:m-0">
      <style>{PRINT_CSS}</style>
      <div className="no-print mb-4 flex items-center justify-between">
        <Button variant="ghost" onClick={() => navigate(-1)} className="gap-2">
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
        <Button onClick={() => window.print()}>
          <Printer className="h-4 w-4" /> {printButtonLabel}
        </Button>
      </div>

      <div className="invoice-wrapper">
        <div className="invoice-content">
          <div className="top-brand">
            <div style={{ width: '30%', fontSize: 11 }} className="print:text-[10px]">
              <div><strong>CR No:</strong> {buyer.otherId || '-'}</div>
              <div><strong>VAT No:</strong> {buyer.vat || '-'}</div>
            </div>
            <div style={{ width: '40%', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <img src={getLogoPath()} alt="Default Logo" style={{ height: 55, objectFit: 'contain', margin: '0 auto' }} className="print:h-10" />
            </div>
            <div style={{ width: '30%', textAlign: 'right', fontSize: 11 }} className="print:text-[10px]">
              <div><strong>رقم تسجيل الشركة:</strong> {buyer.otherId || '-'}</div>
              <div><strong>رقم ضريبة القيمة المضافة:</strong> {buyer.vat || '-'}</div>
            </div>
          </div>

          <div className="print:my-1" style={{ backgroundColor: '#0A1E60', height: '3px', margin: '8px 0' }} />

          <div className="print:mb-2" style={{ textAlign: 'center', marginBottom: 6 }}>
            <div className="company-title print:text-base" style={{ fontSize: 18, fontWeight: 700, marginBottom: 3 }}>{titleAr}</div>
            <div className="company-title print:text-sm" style={{ fontSize: 16, fontWeight: 700 }}>{titleEn}</div>
          </div>

          <table className="info-table print:mb-2" style={{ marginBottom: 6 }}>
            <colgroup>
              <col style={{ width: '25%' }} />
              <col style={{ width: '50%' }} />
              <col style={{ width: '25%' }} />
            </colgroup>
            <tbody>
              {infoRows.map((row) => (
                <tr key={row.en}>
                  <td className="info-label">{row.en}</td>
                  <td className="info-value">{row.value}</td>
                  <td className="info-label-ar rtl"><strong>{row.ar}:</strong></td>
                </tr>
              ))}
            </tbody>
          </table>

          <table className="seller-buyer-table print:mb-2" style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #F5F5F5', marginBottom: 4 }}>
            <thead>
              <tr>
                <th className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', textAlign: 'left', fontWeight: 700, fontSize: 10, backgroundColor: '#F5F5F5' }} colSpan={2}>SELLER</th>
                <th className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', textAlign: 'right', fontWeight: 700, fontSize: 10, backgroundColor: '#F5F5F5' }} />
                <th className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', textAlign: 'right', fontWeight: 700, fontSize: 10, backgroundColor: '#F5F5F5' }} colSpan={3}>BUYER</th>
              </tr>
            </thead>
            <tbody>
              <PartyRow labelEn="Name:" labelAr="الاسم:" left={seller.nameEn || '-'} leftAr={seller.nameAr} right={buyer.nameEn || '-'} rightAr={buyer.nameAr} />
              <PartyRow labelEn="Address:" labelAr="عنوان:" left={seller.address || '-'} leftAr={seller.addressAr} right={buyer.address || '-'} rightAr={buyer.addressAr} />
              <PartyRow labelEn="Country:" labelAr="دولة:" left={seller.country || 'Saudi Arabia (SA)'} right={buyer.country || 'Saudi Arabia (SA)'} />
              <PartyRow labelEn="City:" labelAr="مدينة:" left={seller.city || '-'} right={buyer.city || '-'} />
              <PartyRow labelEn="Zip/Postal Code:" labelAr="الرمز / الكود البريدي:" left={seller.zip || '-'} right={buyer.zip || '-'} />
              <PartyRow labelEn="Phone:" labelAr="هاتف:" left={seller.phone || '-'} right={buyer.phone || '-'} />
              <PartyRow labelEn="VAT Number:" labelAr="رقم ضريبة القيمة المضافة:" left={seller.vat || '-'} right={buyer.vat || '-'} />
              <PartyRow labelEn="Other Seller Id:" labelAr="معرف الآخر:" left={seller.otherId ? `CR# ${seller.otherId}` : '-'} rightLabelEn="Other Buyer Id:" right={buyer.otherId ? `CR# ${buyer.otherId}` : '-'} />
            </tbody>
          </table>

          <table className="items-table">
            <thead>
              <tr>
                <th style={{ width: '5%' }}>SR#</th>
                <th style={{ width: '39%' }}>ITEM & DESCRIPTION</th>
                <th style={{ width: '8%', textAlign: 'right' }}>QTY</th>
                <th style={{ width: '10%', textAlign: 'right' }}>RATE</th>
                <th style={{ width: '10%', textAlign: 'right' }}>{itemAmountLabelEn}</th>
                <th style={{ width: '10%', textAlign: 'right' }}>TAXABLE AMOUNT.</th>
                <th style={{ width: '6%', textAlign: 'right' }}>VAT (%)</th>
                <th style={{ width: '6%', textAlign: 'right' }}>VAT AMT.</th>
                <th style={{ width: '10%', textAlign: 'right' }}>TOTAL {getBranchCurrency()}</th>
              </tr>
              <tr>
                <th>رقم</th>
                <th>البند و الوصف</th>
                <th style={{ textAlign: 'right' }}>الكمية</th>
                <th style={{ textAlign: 'right' }}>السعر</th>
                <th style={{ textAlign: 'right' }}>{itemAmountLabelAr}</th>
                <th style={{ textAlign: 'right' }}>المبلغ الخاضع للضريبة</th>
                <th style={{ textAlign: 'right' }}>ضريبة</th>
                <th style={{ textAlign: 'right' }}>قيمة الضريبة</th>
                <th style={{ textAlign: 'right' }}>إجمالي</th>
              </tr>
            </thead>
            <tbody>
              {lines.length === 0 ? (
                <tr><td colSpan={9} style={{ textAlign: 'center', padding: 16 }}>No line items available</td></tr>
              ) : (
                lines.map((line, idx) => {
                  const taxable = line.taxableAmount ?? line.itemPrice ?? 0
                  return (
                    <tr key={idx}>
                      <td style={{ textAlign: 'center', fontWeight: 700 }}>{idx + 1}</td>
                      <td>{line.description || 'Item'}</td>
                      <td style={{ textAlign: 'right' }}>{formatPrintAmount(line.qty ?? 0)}</td>
                      <td style={{ textAlign: 'right' }}>{formatPrintAmount(line.unitPrice)}</td>
                      <td style={{ textAlign: 'right' }}>{formatPrintAmount(line.itemPrice)}</td>
                      <td style={{ textAlign: 'right' }}>{formatPrintAmount(taxable)}</td>
                      <td style={{ textAlign: 'right' }}>{formatPrintAmount(line.taxRate)}</td>
                      <td style={{ textAlign: 'right' }}>{formatPrintAmount(line.taxAmount)}</td>
                      <td style={{ textAlign: 'right', fontWeight: 700 }}>{formatPrintAmount(line.lineAmount)}</td>
                    </tr>
                  )
                })
              )}
              <tr className="items-total-row">
                <td colSpan={4} style={{ textAlign: 'right' }}>TOTAL</td>
                <td style={{ textAlign: 'right' }}>{formatPrintAmount(totals.subTotal)}</td>
                <td style={{ textAlign: 'right' }}>{formatPrintAmount(totals.taxableAmount)}</td>
                <td />
                <td style={{ textAlign: 'right' }}>{formatPrintAmount(totals.vatAmount)}</td>
                <td style={{ textAlign: 'right' }}>{formatPrintAmount(totals.total)}</td>
              </tr>
            </tbody>
          </table>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 6, marginTop: 0 }} className="print:gap-2 print:mt-0">
            <div>
              <div style={{ fontWeight: 700, marginBottom: 3, fontSize: 9 }} className="print:text-xs print:mb-1"><strong>ملاحظات - Notes</strong></div>
              <div style={{ fontSize: 9 }} className="print:text-xs">{notes || '—'}</div>
              <div style={{ marginTop: 4 }} className="print:mt-2">
                <div style={{ fontWeight: 700, fontSize: 9 }} className="print:text-xs"><strong>Amount In Words:</strong></div>
                <div style={{ fontWeight: 700, fontSize: 9 }} className="print:text-xs">{amountWords || `Zero ${getBranchCurrencyWords().major} Only`}</div>
                <div style={{ fontSize: 8, color: '#555', marginTop: 2 }} className="print:text-[10px] print:mt-1">(All Amount in {getBranchCurrency()})</div>
              </div>
            </div>
            <div className="summary-box print:p-2" style={{ border: '1px solid #F5F5F5', padding: 6 }}>
              <SummaryRow en="المجموع الفرعي - Sub Total" value={totals.subTotal} />
              <SummaryRow en="المبلغ الخاضع للضريبة - Taxable Amount" value={totals.taxableAmount} />
              <SummaryRow en="ضريبة القيمة المضافة - VAT Amount" value={totals.vatAmount} />
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3, fontSize: 9 }} className="print:text-xs print:mb-1">
                <div style={{ fontWeight: 700 }}>ضمان حسن التنفيذ - Retention</div>
                <div style={{ fontWeight: 700 }}>{retention}%</div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '2px solid #F5F5F5', paddingTop: 4, marginTop: 4, fontSize: 10, fontWeight: 800 }} className="print:text-xs print:pt-2 print:mt-2">
                <div>إجمالي المبلغ المستحق - Total Amount Due</div>
                <div>{formatPrintAmount(totals.total)}</div>
              </div>
            </div>
          </div>
        </div>
        <div className="invoice-footer">{footer}</div>
      </div>
    </div>
  )
}

function SummaryRow({ en, value }: { en: string; value: number }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3, fontSize: 9 }} className="print:text-xs print:mb-1">
      <div style={{ fontWeight: 700 }}>{en}</div>
      <div style={{ fontWeight: 700 }}>{formatPrintAmount(value)}</div>
    </div>
  )
}

function PartyRow({
  labelEn,
  labelAr,
  left,
  leftAr,
  right,
  rightAr,
  rightLabelEn,
}: {
  labelEn: string
  labelAr: string
  left: string
  leftAr?: string
  right: string
  rightAr?: string
  rightLabelEn?: string
}) {
  return (
    <tr>
      <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, width: '12%', verticalAlign: 'middle' }}><strong>{labelEn}</strong></td>
      <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, width: '20%', wordWrap: 'break-word', overflowWrap: 'break-word', verticalAlign: 'middle' }}>
        {left}
        {leftAr ? <div className="rtl print:text-[10px]" style={{ fontSize: 10, marginTop: 2, textAlign: 'right' }}>{leftAr}</div> : null}
      </td>
      <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, textAlign: 'right', width: '8%', verticalAlign: 'middle' }}><strong>{labelAr}</strong></td>
      <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, width: '12%', verticalAlign: 'middle' }}><strong>{rightLabelEn || labelEn}</strong></td>
      <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, width: '20%', wordWrap: 'break-word', overflowWrap: 'break-word', verticalAlign: 'middle' }}>
        {right}
        {rightAr ? <div className="rtl print:text-[10px]" style={{ fontSize: 10, marginTop: 2, textAlign: 'right' }}>{rightAr}</div> : null}
      </td>
      <td className="print:text-xs print:p-1" style={{ border: '1px solid #F5F5F5', padding: '4px', fontSize: 11, textAlign: 'right', width: '8%', verticalAlign: 'middle' }}><strong>{labelAr}</strong></td>
    </tr>
  )
}
